// ABOUTME: React Query hook for fetching order cancellation lead time configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/order-cancellation-lead-times, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {api} from '../../../../App.store'
import {OrderCancellationLeadTimeApiResponse, OrderCancellationLeadTimeConfig} from '../types/orderCancellationLeadTime'
import {OrderIntakeLeadTimeFormValues} from '../types/orderIntakeLeadTime'
import {transformLeadTimeResponse} from '../utils/leadTimeTransformers'
import {asNotInherited, emptyApiRow} from '../utils/emptyConfig'

const extractFormValues = (config: OrderCancellationLeadTimeConfig): OrderIntakeLeadTimeFormValues => ({
  farDistanceThreshold: config.farDistanceThreshold,
  farDistanceThresholdUomId: config.farDistanceThresholdUomId ?? '',
  farDistanceThresholdUomDisplay: config.farDistanceThresholdUomDisplay,
  standard: config.standard,
  nonStandard: config.nonStandard,
})

export const useOrderCancellationLeadTimeConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: OrderCancellationLeadTimeConfig | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery<OrderCancellationLeadTimeApiResponse[], Error>({
    queryKey: ['orderCancellationLeadTimeConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<OrderCancellationLeadTimeApiResponse[]>('/tuc-configs/order-cancellation-lead-times', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined
    const rootScopeData = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const rootConfig = rootScopeData ? transformLeadTimeResponse(rootScopeData) : undefined
    const parentValues: OrderIntakeLeadTimeFormValues | undefined = rootConfig
      ? extractFormValues(rootConfig)
      : undefined

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) return transformLeadTimeResponse(match, parentValues)
    // No override for this scope — return root config as inherited baseline
    if (!rootScopeData) {
      return asNotInherited(transformLeadTimeResponse(emptyApiRow<OrderCancellationLeadTimeApiResponse>(scopeId)))
    }
    const inherited = transformLeadTimeResponse(rootScopeData)
    return {
      ...inherited,
      id: undefined,
      _meta: {...inherited._meta, isInherited: true},
      overrides: {farDistanceThreshold: false, leadTimes: false},
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
